import React from 'react';
import BlurFade from './public/motion/BlurFade';

const COLORS = [
  { name: 'Ink', hex: '#f3ebe2', varName: '--dusk-ink', swatch: '#f3ebe2', onDark: true },
  { name: 'Muted', hex: '#9a8f84', varName: '--dusk-muted', swatch: '#9a8f84', onDark: true },
  { name: 'Faint', hex: '#5c534c', varName: '--dusk-faint', swatch: '#5c534c', onDark: true },
  { name: 'Copper', hex: '#d00068', varName: '--dusk-copper', swatch: '#d00068', onDark: false },
  { name: 'Amber', hex: '#f23d86', varName: '--dusk-amber', swatch: '#f23d86', onDark: false },
  { name: 'Background', hex: '#0a0908', varName: '--dusk-bg', swatch: '#0a0908', onDark: false },
];

const LOGO_ASSETS = [
  {
    src: '/images/brand/logo_d.png',
    fallback: '/images/logo.png',
    title: 'Primary mark',
    note: 'For dark surfaces and the site chrome.',
    previewBg: 'var(--dusk-bg)',
    download: '/images/brand/logo_d.png',
    format: 'PNG',
  },
  {
    src: '/images/brand/logo_w.png',
    fallback: '/images/logo.png',
    title: 'Light surface',
    note: 'Use on pale / print backgrounds only.',
    previewBg: '#f3ebe2',
    download: '/images/brand/logo_w.png',
    format: 'PNG',
  },
  {
    src: '/images/brand/logo.jpg',
    fallback: '/images/logo.png',
    title: 'Square crop',
    note: '300×300 — avatars, embeds, social.',
    previewBg: 'var(--dusk-bg)',
    download: '/images/brand/logo.jpg',
    format: 'JPEG',
    square: true,
  },
];

const BrandKitDisplay: React.FC = () => {
  return (
    <>
      <section className="dusk-section !pt-16 md:!pt-24 !pb-10">
        <div className="dusk-container">
          <BlurFade>
            <p className="text-[0.7rem] font-semibold tracking-[0.16em] uppercase text-[var(--dusk-copper)] m-0 mb-4">
              Press &amp; partners
            </p>
            <h1 className="dusk-page-title text-[clamp(2.5rem,6vw,4rem)] m-0 mb-5 leading-tight">
              Brand Kit
            </h1>
            <p className="text-[var(--dusk-muted)] text-base md:text-lg leading-relaxed max-w-xl m-0">
              Marks, palette, and type for DUSKPROOF. Keep copper as the accent — don’t recolor the logo or stretch
              proportions.
            </p>
          </BlurFade>
        </div>
      </section>

      <section className="dusk-section !pt-4 !pb-16">
        <div className="dusk-container">
          <BlurFade>
            <div className="border-t border-[var(--dusk-line)] pt-10 mb-16">
              <h2 className="font-display text-2xl md:text-3xl text-[var(--dusk-ink)] m-0 mb-3 font-normal">
                Logo
              </h2>
              <p className="text-[var(--dusk-muted)] text-sm md:text-base leading-relaxed m-0 max-w-2xl mb-10">
                Clear space ≈ half the mark height. Prefer PNG with transparency. Don’t add outlines, drop shadows, or
                rotate the lockup.
              </p>

              <div className="grid gap-0 border-t border-[var(--dusk-line)]">
                {LOGO_ASSETS.map((asset, i) => (
                  <div
                    key={asset.title}
                    className="grid md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] gap-6 md:gap-10 py-8 border-b border-[var(--dusk-line)] items-center"
                  >
                    <div
                      className="aspect-[16/10] flex items-center justify-center border border-[var(--dusk-line)] px-6"
                      style={{ background: asset.previewBg }}
                    >
                      <img
                        src={asset.src}
                        alt=""
                        className={asset.square ? 'max-h-24 w-auto' : 'max-h-16 w-auto max-w-full'}
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.src.endsWith(asset.fallback)) {
                            img.style.display = 'none';
                            return;
                          }
                          img.src = asset.fallback;
                        }}
                      />
                    </div>
                    <div>
                      <p className="text-[0.65rem] font-semibold tracking-[0.14em] uppercase text-[var(--dusk-faint)] m-0 mb-2">
                        Asset {String(i + 1).padStart(2, '0')}
                      </p>
                      <h3 className="font-display text-xl text-[var(--dusk-ink)] m-0 mb-2 font-normal">
                        {asset.title}
                      </h3>
                      <p className="text-sm text-[var(--dusk-muted)] m-0 mb-5 leading-relaxed">{asset.note}</p>
                      <a
                        href={asset.download}
                        download
                        className="inline-flex items-center gap-2 text-[0.72rem] font-semibold tracking-[0.12em] uppercase text-[var(--dusk-copper)] no-underline hover:text-[var(--dusk-amber)]"
                      >
                        Download {asset.format} →
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BlurFade>

          <BlurFade delay={0.05}>
            <div className="border-t border-[var(--dusk-line)] pt-10 mb-16">
              <h2 className="font-display text-2xl md:text-3xl text-[var(--dusk-ink)] m-0 mb-3 font-normal">
                Palette
              </h2>
              <p className="text-[var(--dusk-muted)] text-sm md:text-base leading-relaxed m-0 max-w-2xl mb-10">
                Copper is the brand signal. Ink and muted handle body type on the near-black background.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--dusk-line)] border border-[var(--dusk-line)]">
                {COLORS.map((c) => (
                  <div key={c.name} className="bg-[var(--dusk-bg)]">
                    <div className="h-24 md:h-28" style={{ backgroundColor: c.swatch }} />
                    <div className="p-4 border-t border-[var(--dusk-line)]">
                      <p className="text-sm text-[var(--dusk-ink)] m-0 mb-1">{c.name}</p>
                      <p className="text-xs text-[var(--dusk-muted)] m-0 font-mono tabular-nums">{c.hex}</p>
                      <p className="text-[0.65rem] text-[var(--dusk-faint)] m-0 mt-1 font-mono">{c.varName}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </BlurFade>

          <BlurFade delay={0.08}>
            <div className="border-t border-[var(--dusk-line)] pt-10 pb-8">
              <h2 className="font-display text-2xl md:text-3xl text-[var(--dusk-ink)] m-0 mb-3 font-normal">
                Typography
              </h2>
              <p className="text-[var(--dusk-muted)] text-sm md:text-base leading-relaxed m-0 max-w-2xl mb-10">
                Display for titles; body for UI and long copy. Both ship with the public site.
              </p>

              <div className="grid md:grid-cols-2 gap-10 md:gap-16">
                <div>
                  <p className="text-[0.65rem] font-semibold tracking-[0.14em] uppercase text-[var(--dusk-copper)] m-0 mb-3">
                    Display · Fraunces
                  </p>
                  <p
                    className="font-display text-[clamp(2rem,4vw,3rem)] text-[var(--dusk-ink)] m-0 leading-tight font-normal"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    Institution-grade staking
                  </p>
                </div>
                <div>
                  <p className="text-[0.65rem] font-semibold tracking-[0.14em] uppercase text-[var(--dusk-copper)] m-0 mb-3">
                    Body · DM Sans
                  </p>
                  <p
                    className="text-base md:text-lg text-[var(--dusk-muted)] m-0 leading-relaxed"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    Secure, non-custodial staking powered by high-performance DUSKPROOF validator nodes — built for
                    reliability, scale, and consistent rewards.
                  </p>
                </div>
              </div>
            </div>
          </BlurFade>
        </div>
      </section>
    </>
  );
};

export default BrandKitDisplay;
